import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Form, Alert, Badge } from 'react-bootstrap';
import { FaUpload, FaCog, FaCheck, FaSync, FaChevronDown, FaChevronUp, FaStore } from 'react-icons/fa';
import { toast } from 'react-toastify';
import {
  createRappiMenuFromLocalId,
  activateRappiStore
} from '../../../../../../../../crud/pedidos_api/rappi';
import {
  initializeTPPConfig,
  getTPPConfig
} from '../../../../../../../../crud/management_api/third_party_provider_config';
import MiniLoadingSpinner from '../../../../../../../../components/Spinners/MiniLoadingSpinner';
import './Rappi.scss';

const Rappi: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [loading, setLoading] = useState(false);
  const [configData, setConfigData] = useState<any>(null);
  const [configNotFound, setConfigNotFound] = useState(false);
  const [catalogStatus, setCatalogStatus] = useState<any>(null);
  
  // Section visibility states
  const [storesExpanded, setStoresExpanded] = useState(false);
  const [menuExpanded, setMenuExpanded] = useState(false);
  const [configExpanded, setConfigExpanded] = useState(false);
  
  // Form states for configuration
  const [integrationId, setIntegrationId] = useState('');
  const [storeId, setStoreId] = useState('');
  const [storeIntegrationId, setStoreIntegrationId] = useState('');

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const localId = user.local_id;

  const renderProviderLogo = () => {
    return(
      <div className="provider-logo-small">
        <img 
          alt="Rappi logo"
          className="provider-logo-img"
          src={`${process.env.PUBLIC_URL}/icons/providers/rappi.png`} 
        />
      </div>
    )
  }


  const loadConfiguration = useCallback(async () => {
    try {
      const response: any = await getTPPConfig({
        local_id: localId,
        third_party_provider_id: 2 // Rappi
      });
      
      if (response.data) {
        setConfigData(response.data);
        setConfigNotFound(false);
        const config = response.data.config?.object || {};
        setIntegrationId(config.integration_id || '');
        setStoreId(config.store_id || '');
        setStoreIntegrationId(config.store_integration_id || '');
      }
    } catch (error: any) {
      console.error('Error loading configuration:', error);
      if (error.response?.status === 404) {
        setConfigNotFound(true);
        setConfigData(null);
      } else {
        toast.error('Error cargando configuración');
      }
    }
  }, [localId]);

  useEffect(() => {
    const loadInitialData = async () => {
      if (isExpanded) {
        setLoading(true);
        try {
          await Promise.all([
            loadConfiguration()
          ]);
        } catch (error) {
          console.error('Error loading Rappi data:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded]); // loadStores and loadConfiguration are stable callbacks

  const handleUpdateMenu = async () => {
    setLoading(true);
    try {
      const response: any = await createRappiMenuFromLocalId(localId);
      setCatalogStatus(response.data);
      toast.success('Menú actualizado exitosamente en Rappi');
    } catch (error: any) {
      console.error('Error updating menu:', error);
      toast.error(`Error actualizando menú: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleActivateStore = async (storeId: number) => {
    setLoading(true);
    try {
      await activateRappiStore(storeId);
      toast.success('Tienda activada exitosamente en Rappi');
    } catch (error: any) {
      console.error('Error activating store:', error);
      toast.error(`Error activando tienda: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfiguration = async () => {
    if (!integrationId || !storeId || !storeIntegrationId) {
      toast.error('Todos los campos de configuración son obligatorios');
      return;
    }

    setLoading(true);
    try {
      const configPayload = {
        local_id: localId,
        third_party_provider_id: 2, // Rappi
        external_local_id: storeId,
        config: {
          integration_id: integrationId,
          store_id: storeId,
          store_integration_id: storeIntegrationId
        }
      };

      await initializeTPPConfig(configPayload);
      toast.success('Configuración guardada exitosamente');
      setConfigNotFound(false);
      await loadConfiguration();
    } catch (error: any) {
      console.error('Error saving configuration:', error);
      toast.error(`Error guardando configuración: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const renderStoresSection = () => (
    <div className="stores-section">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5>Gestión de Tiendas</h5>
        <button
          className="mp-button secondary"
          disabled={loading}
        >
          <FaSync className="me-2" style={{color: 'var(--text-color)'}} />
          Actualizar
        </button>
      </div>
      
      <Alert variant="warning" className="mt-3">
        <strong>Nota:</strong> Rappi no tiene endpoints de apertura/cierre de tienda disponibles en la configuración actual.
      </Alert>
    </div>
  );

  const renderMenuSection = () => (
    <div className="menu-section">
      {configNotFound && (
        <Alert variant="warning" className="mb-3">
          <strong>Configuración requerida:</strong> Para actualizar el menú en Rappi, primero necesitas:
          <ol className="mb-0 mt-1">
            <li>Crear la configuración de credenciales en la sección "Configuración"</li>
            <li>Luego actualizar el menú, que tomará el menú de Medio Punto y lo cargará en Rappi</li>
          </ol>
        </Alert>
      )}
      
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5>Gestión de Menú</h5>
        {catalogStatus && (
          <Badge bg="info">
            Última actualización: {new Date(catalogStatus.timestamp || Date.now()).toLocaleString()}
          </Badge>
        )}
      </div>
      
      <div className="menu-controls">
        <button
          className="mp-button primary"
          onClick={handleUpdateMenu}
          disabled={loading || configNotFound}
        >
          {loading ? (
            <MiniLoadingSpinner />
          ) : (
            <FaUpload className="me-2" style={{color: 'var(--text-color)'}} />
          )}
          Actualizar Menú
        </button>
      </div>

      {catalogStatus && (
        <Alert variant={catalogStatus.success ? 'success' : 'warning'} className="mt-3">
          <div><strong>Estado:</strong> {catalogStatus.success ? 'Actualizado exitosamente' : 'Error en actualización'}</div>
          {catalogStatus.message && (
            <div className="mt-1"><small>{catalogStatus.message}</small></div>
          )}
        </Alert>
      )}
    </div>
  );

  const renderConfigurationSection = () => (
    <div className="configuration-section">
      <h5 className="mb-3">Configuración de Credenciales</h5>
      
      {configNotFound && (
        <Alert variant="warning" className="mb-3">
          <strong>No se encontró configuración:</strong> Necesitas crear una configuración para conectar con Rappi. 
          Completa los campos a continuación y guarda la configuración.
        </Alert>
      )}
      
      <Form>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Integration ID *</Form.Label>
              <Form.Control
                type="text"
                value={integrationId}
                onChange={(e) => setIntegrationId(e.target.value)}
                placeholder="ej: INTEGRATION_123"
              />
              <Form.Text className="text-muted">
                ID único de la instancia de integración de Rappi
              </Form.Text>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Store ID *</Form.Label>
              <Form.Control
                type="text"
                value={storeId}
                onChange={(e) => setStoreId(e.target.value)}
                placeholder="ej: 900159485"
              />
              <Form.Text className="text-muted">
                ID de la tienda de Rappi
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Store Integration ID *</Form.Label>
              <Form.Control
                type="text"
                value={storeIntegrationId}
                onChange={(e) => setStoreIntegrationId(e.target.value)}
                placeholder="ej: STORE_INT_123"
              />
              <Form.Text className="text-muted">
                ID de integración de la tienda
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>

        <div className="configuration-controls">
          <button
            className="mp-button success"
            onClick={handleSaveConfiguration}
            disabled={loading}
          >
            {loading ? (
              <MiniLoadingSpinner />
            ) : (
              <FaCog className="me-2" style={{color: 'var(--text-color)'}} />
            )}
            Guardar Configuración
          </button>

          {configData && (
            <button
              className="mp-button secondary ms-2"
              onClick={loadConfiguration}
              disabled={loading}
            >
              <FaSync className="me-2" style={{color: 'var(--text-color)'}} />
              Recargar
            </button>
          )}
        </div>
      </Form>

      {configData && (
        <Alert variant="success" className="mt-3">
          <FaCheck className="me-2" style={{color: 'var(--mediopunto-orange)'}} />
          Configuración activa desde: {new Date(configData.config?.timestamp || Date.now()).toLocaleString()}
        </Alert>
      )}
    </div>
  );

  return (
    <div className="rappi-card">
      <div 
        className="rappi-header"
        onClick={() => setIsExpanded(!isExpanded)}
        style={{ cursor: 'pointer' }}
      >
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            {renderProviderLogo()}
            <h4 className="mb-0 ms-4">Rappi</h4>
          </div>
          <div className="d-flex align-items-center">
            {configData && !configNotFound ? (
              <Badge bg="success" className="me-2">Configurado</Badge>
            ) : configNotFound ? (
              <Badge bg="warning" className="me-2">Sin Configurar</Badge>
            ) : null}
            {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
          </div>
        </div>
      </div>
      
      {isExpanded && (
        <div className="rappi-body">
          {loading && !configData ? (
            <div className="text-center py-3">
              <MiniLoadingSpinner />
            </div>
          ) : (
            <div className="sections-container">
              <div className="section-item">
                <div 
                  className="section-header"
                  onClick={() => setStoresExpanded(!storesExpanded)}
                >
                  <FaStore className="me-3" style={{color: 'var(--text-color)'}} />
                  <span className="fw-medium">Gestión de Tiendas</span>
                  {configNotFound &&
                    <Badge bg="warning" className="ms-auto me-2">Sin Configurar</Badge>
                 }
                  <FaChevronDown className={`chevron-icon ${storesExpanded ? 'expanded' : ''}`} />
                </div>
                <div className={`section-content ${storesExpanded ? 'expanded' : ''}`}>
                  {renderStoresSection()}
                </div>
              </div>
              
              <div className="section-item">
                <div 
                  className="section-header"
                  onClick={() => setMenuExpanded(!menuExpanded)}
                >
                  <FaUpload className="me-3" style={{color: 'var(--text-color)'}} />
                  <span className="fw-medium">Actualizar Menú</span>
                  {configNotFound ? (
                    <Badge bg="warning" className="ms-auto me-2">Sin Configurar</Badge>
                  ) : catalogStatus ? (
                    <Badge 
                      bg={catalogStatus.success ? 'success' : 'warning'} 
                      className="ms-auto me-2"
                    >
                      {catalogStatus.success ? 'Actualizado' : 'Pendiente'}
                    </Badge>
                  ) : null}
                  <FaChevronDown className={`chevron-icon ${menuExpanded ? 'expanded' : ''}`} />
                </div>
                <div className={`section-content ${menuExpanded ? 'expanded' : ''}`}>
                  {renderMenuSection()}
                </div>
              </div>
              
              <div className="section-item">
                <div 
                  className="section-header"
                  onClick={() => setConfigExpanded(!configExpanded)}
                >
                  <FaCog className="me-3" style={{color: 'var(--text-color)'}} />
                  <span className="fw-medium">Configuración</span>
                  {configData && !configNotFound ? (
                    <Badge bg="success" className="ms-auto me-2">Configurado</Badge>
                  ) : configNotFound ? (
                    <Badge bg="warning" className="ms-auto me-2">Sin Configurar</Badge>
                  ) : null}
                  <FaChevronDown className={`chevron-icon ${configExpanded ? 'expanded' : ''}`} />
                </div>
                <div className={`section-content ${configExpanded ? 'expanded' : ''}`}>
                  {renderConfigurationSection()}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Rappi;